import { Collapse, Typography } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

export default function HelpSection() {
	return (
		<Collapse
			bordered={false}
			defaultActiveKey={[]}
			expandIcon={({ isActive }) => (
				<CaretRightOutlined rotate={isActive ? 90 : 0} />
			)}
			className="bg-slate-50"
			items={[
				{
					key: '1',
					label: 'What is a Sagitta?',
					children: (
						<div className="space-y-2">
							<Paragraph className="!mb-2">
								The <Text strong>sagitta</Text> is the height of an arc
								segment, measured as the perpendicular distance from the
								midpoint of the chord to the arc. The word comes from Latin for
								&ldquo;arrow.&rdquo; We use the variable <Text code>s</Text>{' '}
								for sagitta in this tool.
							</Paragraph>
							<svg
								viewBox="0 0 200 100"
								className="w-full max-w-sm border border-slate-200 rounded bg-white p-1"
								aria-hidden
							>
								<line
									x1="30"
									y1="75"
									x2="170"
									y2="75"
									stroke="#1e3a5f"
									strokeWidth="1.5"
								/>
								<path
									d="M 30 75 Q 100 20 170 75"
									fill="none"
									stroke="#2563eb"
									strokeWidth="1.5"
								/>
								<line
									x1="100"
									y1="75"
									x2="100"
									y2="32"
									stroke="#dc2626"
									strokeWidth="2.5"
								/>
								<text x="12" y="80" fontSize="9" className="font-sans">
									c
								</text>
								<text
									x="105"
									y="50"
									fontSize="9"
									fill="#b91c1c"
									className="font-sans"
								>
									s
								</text>
								<text
									x="150"
									y="60"
									fontSize="8"
									fill="#94a3b8"
									className="font-sans"
								>
									R
								</text>
							</svg>
						</div>
					),
				},
				{
					key: '2',
					label: 'When to Use This Tool',
					children: (
						<Paragraph>
							Our vision measurement system can accurately measure small-radius
							arcs by fitting a circle to the captured arc segment. When the
							radius is large and we only capture a minor segment, the system
							cannot reliably determine the radius. This tool converts the
							radius dimension from the customer drawing into an equivalent
							sagitta (chord-height) measurement that our vision system can
							capture accurately.
						</Paragraph>
					),
				},
				{
					key: '3',
					label: 'How to Use',
					children: (
						<ol className="list-decimal pl-4 space-y-1 text-sm">
							<li>Click the button to open the Arc Sagitta Calculator.</li>
							<li>
								Select your unit system (Inches or Millimeters) to match the
								customer drawing. You can switch units at any time — all values
								will be automatically converted.
							</li>
							<li>
								From the customer drawing, identify the radius (or diameter) of
								the arc and the chord length. The chord is the straight-line
								distance between the two endpoints of the arc segment you will
								measure.
							</li>
							<li>
								Enter the chord length and radius (or diameter) into the tool.
							</li>
							<li>
								Optionally, enter the tolerance on the radius from the drawing.
								If the tolerance is symmetric (e.g., ±0.005 in), use the first
								box only. If asymmetric (e.g., +0.010 / −0.005), put the plus
								tolerance in the first box and the minus in the second.
							</li>
							<li>
								The tool shows the nominal sagitta and, when applicable,
								converted tolerance bounds. Two rounding levels are shown —
								pick the one that matches your vision system input precision.
							</li>
							<li>
								Use the copy buttons, then paste values into the ballooning
								application.
							</li>
							<li>
								Close the dialog when done. Your values are saved and remain
								when you reopen it.
							</li>
						</ol>
					),
				},
				{
					key: '4',
					label: 'Setting Up the Vision System Measurement',
					children: (
						<ol className="list-decimal pl-4 space-y-2 text-sm">
							<li>
								Use <Text strong>Basics</Text> to create the arc.
							</li>
							<li>
								Use <Text strong>Basics</Text> to create lines which intersect
								the arc at the endpoints.
							</li>
							<li>
								Use the <Text strong>Virtual</Text> tool to create
								intersections.
							</li>
							<li>
								Use the <Text strong>Virtual</Text> tool to create a line
								between the intersections (Chord).
							</li>
							<li>
								Use the <Text strong>Virtual</Text> tool to create the midpoint
								of this line (Chord).
							</li>
							<li>
								Use <Text strong>Basics</Text> to create a measurement,{' '}
								<Text strong>Circle to Point</Text>, between the circle and
								the midpoint. Select the <Text strong>Min</Text> setting for
								the circle.
							</li>
							<li>
								Enter the <Text strong>Nominal Sagitta</Text> as the nominal
								measurement.
							</li>
							<li>
								Enter the tolerance as the <Text strong>Drawing Tolerance</Text>{' '}
								or <Text strong>Radius Adjusted Tolerance</Text> using your
								best judgement.
							</li>
						</ol>
					),
				},
				{
					key: '5',
					label: 'Tolerance Conversion Notes',
					children: (
						<div className="space-y-2 text-sm">
							<Paragraph>
								The relationship between radius and sagitta is nonlinear and
								inverse. A larger radius produces a smaller (flatter) sagitta.
								Because of this, a symmetric radius tolerance (e.g., ±0.005 in)
								converts to a slightly asymmetric sagitta tolerance. For most
								practical cases with tight tolerances, the asymmetry is
								negligible. In many cases, the title block default tolerance
								can be applied directly to the sagitta; use the conversion
								when precision is critical.
							</Paragraph>
							<Paragraph className="!mb-0">
								<Text strong>Example:</Text> R = 24 in, c = 3.5 in, radius
								tolerance ±0.005 in. Nominal s ≈ 0.063906 in; the
								radius-adjusted sagitta limits are about +0.000013 in / −0.000013
								in, with drawing tolerance restated as ±0.005 in.
							</Paragraph>
						</div>
					),
				},
			]}
		/>
	);
}
